import apiClient, { type PagedResponse } from '../client'
import type { PersonResponse, DniLookupResponse } from '../models/response'
import type { RoleResponse } from '../models/response/usuario'
import type {
  CreatePersonStep1Request,
  UpdatePersonStep2Request,
  FinalizePersonRequest,
  UpdatePersonRequest,
} from '../models/request'

const BASE_PATH = '/persons'

export const personsService = {
  async getAll(params?: {
    page?: number
    pageSize?: number
    search?: string
    roleCode?: string
  }): Promise<PagedResponse<PersonResponse>> {
    return apiClient.get<PagedResponse<PersonResponse>>(BASE_PATH, params)
  },

  async getById(id: string): Promise<PersonResponse> {
    return apiClient.get<PersonResponse>(`${BASE_PATH}/${id}`)
  },

  async searchByDoc(identityDoc: string): Promise<PersonResponse | null> {
    return apiClient.get<PersonResponse | null>(`${BASE_PATH}/search-by-doc`, { identityDoc })
  },

  async lookupDni(dni: string, documentType?: number): Promise<DniLookupResponse> {
    const params: Record<string, unknown> = { dni }
    if (documentType !== undefined) params.documentType = documentType
    return apiClient.get<DniLookupResponse>(`${BASE_PATH}/lookup-dni`, params)
  },


  async getRoles(): Promise<RoleResponse[]> {
    return apiClient.get<RoleResponse[]>(`${BASE_PATH}/roles`)
  },

  async createStep1(data: CreatePersonStep1Request): Promise<PersonResponse> {
    return apiClient.post<PersonResponse, CreatePersonStep1Request>(`${BASE_PATH}/step1`, data)
  },

  async updateStep2(id: string, data: UpdatePersonStep2Request): Promise<PersonResponse> {
    return apiClient.put<PersonResponse, UpdatePersonStep2Request>(`${BASE_PATH}/${id}/step2`, data)
  },

  async finalize(id: string, data: FinalizePersonRequest): Promise<PersonResponse> {
    return apiClient.post<PersonResponse, FinalizePersonRequest>(`${BASE_PATH}/${id}/finalize`, data)
  },

  async update(id: string, data: UpdatePersonRequest): Promise<PersonResponse> {
    return apiClient.put<PersonResponse, UpdatePersonRequest>(`${BASE_PATH}/${id}`, data)
  },

  /** Rotates the temp password and resends the welcome email. Server rejects
   * once the user has logged in at least once. */
  async resendInvitation(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/resend-invitation`)
  },

  /** Re-sends the QR + setup instructions for users with mobile-app roles. */
  async resendMobileSetup(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/resend-mobile-setup`)
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}`)
  },
}
